package com.trevorwiebe.timesheet.calendar.presentation

import com.trevorwiebe.timesheet.core.presentation.DisposableViewModel
import androidx.lifecycle.viewModelScope
import com.trevorwiebe.timesheet.calendar.domain.usecases.DeleteTimeOffRequest
import com.trevorwiebe.timesheet.calendar.domain.usecases.GetCalendarStructure
import com.trevorwiebe.timesheet.calendar.domain.usecases.GetTimeOffRequests
import com.trevorwiebe.timesheet.calendar.domain.usecases.PostTimeOffRequest
import com.trevorwiebe.timesheet.calendar.domain.usecases.ReviewTimeOffRequest
import com.trevorwiebe.timesheet.calendar.domain.vibrate
import com.trevorwiebe.timesheet.calendar.presentation.uiHelper.TimeOffRequestUi
import com.trevorwiebe.timesheet.core.data.FirestoreListenerRegistry
import com.trevorwiebe.timesheet.core.domain.CoreRepository
import com.trevorwiebe.timesheet.core.domain.Util.localDateTime
import com.trevorwiebe.timesheet.core.domain.model.DatabaseUserModel
import com.trevorwiebe.timesheet.core.domain.model.TimeOffRequestModel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate

class CalendarViewModel(
    val postTimeOffRequest: PostTimeOffRequest,
    val getTimeOff: GetTimeOffRequests,
    val deleteTimeOffRequest: DeleteTimeOffRequest,
    val reviewTimeOffRequest: ReviewTimeOffRequest,
    private val coreRepository: CoreRepository,
) : DisposableViewModel() {

    private val _state = MutableStateFlow(CalendarState())
    val state = _state.asStateFlow()

    init {
        getSignedInUser()
        buildCalendarStructure()
        getTimeOffRequests()
    }

    fun onEvent(event: CalendarEvent) {
        when (event) {
            is CalendarEvent.OnSetCalendarType -> {
                _state.update { it.copy(calendarType = event.type) }
            }

            is CalendarEvent.OnSetAddTimeOffMode -> {
                _state.update { it.copy(timeOffMode = event.mode) }
                if (!event.mode) {
                    // finished selecting time off dates

                    if (event.saving) {
                        // submit time off request
                        submitTimeOffRequest()
                    } else {
                        unselectAllDates()
                    }
                } else {
                    vibrate()
                }
            }

            is CalendarEvent.OnSetSelectedTimeOff -> {
                val dayUiList = _state.value.calendarStructure.toMutableList()
                val dayIndex = dayUiList.indexOfFirst { it.date == event.date }

                if (dayIndex != -1) {
                    val updatedDay = dayUiList[dayIndex].toggleTimeOff
                    dayUiList[dayIndex] = updatedDay

                    _state.update {
                        it.copy(calendarStructure = dayUiList)
                    }
                }
            }
            is CalendarEvent.OnTimeOffSelected -> {
                _state.update { it.copy(timeOffModel = event.timeOffRequest) }
            }

            is CalendarEvent.OnDeleteTimeOffRequest -> {
                val request = event.timeOffRequestModel?.request
                val requestId = request?.id
                val employeeId = request?.employeeId
                if (requestId != null && employeeId != null) {
                    removeTimeOffRequest(employeeId, requestId)
                }
            }

            is CalendarEvent.OnReviewTimeOffRequest -> {
                val request = event.timeOffRequestModel?.request
                val requestId = request?.id
                val employeeId = request?.employeeId
                if (requestId != null && employeeId != null) {
                    submitReview(employeeId, requestId, event.approved)
                }
            }
        }
    }

    private fun submitReview(
        employeeId: String,
        requestId: String,
        approved: Boolean,
    ) {
        viewModelScope.launch {
            reviewTimeOffRequest(employeeId, requestId, approved)
        }
    }

    private fun getSignedInUser() {
        viewModelScope.launch {
            val response = coreRepository.getDatabaseUser()
            val user = response.data as DatabaseUserModel
            _state.update {
                it.copy(user = user)
            }
        }
    }

    @Suppress("UNCHECKED_CAST")
    private suspend fun getUserList() {
        val response = coreRepository.getUserList(includeDeleted = true)
        val userList = response.data as List<DatabaseUserModel>
        _state.update {
            it.copy(userList = userList)
        }
    }

    private fun buildCalendarStructure() {
        val getCalendarStructure = GetCalendarStructure()

        val calendarStructure = getCalendarStructure()
        _state.update {
            it.copy(calendarStructure = calendarStructure)
        }
    }

    private fun submitTimeOffRequest() {
        viewModelScope.launch {
            val user = _state.value.user
            val timeOffList = _state.value.calendarStructure
                .filter { it.selectedForTimeOff }
                .map { dayUi ->
                    TimeOffRequestModel(
                        id = null,
                        employeeId = user?.id,
                        requestOffTime = dayUi.date,
                        requestSubmittedDateTime = localDateTime(),
                        approved = null,
                        reviewedDate = null
                    )
                }

            if (timeOffList.isEmpty()) return@launch
            postTimeOffRequest(timeOffList)
        }
    }

    @Suppress("UNCHECKED_CAST")
    private fun getTimeOffRequests() {
        val job = viewModelScope.launch {

            getUserList()

            getTimeOff().collect {
                if (it.error == null) {
                    val timeOffList = it.data as List<TimeOffRequestModel>

                    val timeOffListUi = timeOffList.map { timeOffRequestModel ->

                        val employee = _state.value.userList.find { userModel ->
                            userModel.id == timeOffRequestModel.employeeId
                        }

                        val employeeName = employee?.displayName
                            ?.takeIf { it.isNotEmpty() } ?: "Unknown"

                        TimeOffRequestUi(
                            request = timeOffRequestModel,
                            employeeName = employeeName
                        )
                    }

                    // Group requests by date, ordered by submission time within each day
                    val requestsByDate: Map<LocalDate, List<TimeOffRequestUi>> =
                        timeOffListUi
                            .sortedBy { it.request.requestSubmittedDateTime }
                            .groupBy { timeOffRequest ->
                                timeOffRequest.request.requestOffTime
                            }

                    val newDayUiList = _state.value.calendarStructure.map { dayUi ->
                        val requestsForDay = requestsByDate[dayUi.date].orEmpty()

                        dayUi.copy(employeesOff = requestsForDay)
                    }

                    _state.update { it.copy(calendarStructure = newDayUiList) }
                }

                // clear selected dates
                unselectAllDates()
            }
        }
        FirestoreListenerRegistry.registerJob(job)
    }

    private fun unselectAllDates() {
        val dayUiList = _state.value.calendarStructure.map { it.copy(selectedForTimeOff = false) }
        _state.update { it.copy(calendarStructure = dayUiList) }
    }

    private fun removeTimeOffRequest(employeeId: String, timeOffId: String) {
        viewModelScope.launch {
            deleteTimeOffRequest(employeeId, timeOffId)
        }
    }
}